import { useLocation } from 'react-router-dom'
import { useQuery as useReactQuery } from '@tanstack/react-query'
import { useQuery } from '../../../routes'
import { humanize, useDebounce } from 'utils'
import { flat } from 'utils'
import { useFetchWithAuth } from '../../../session/useFetchWithAuth'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

interface AdminProfileSearchResponse {
  search: {
    success: boolean
    profiles: {
      nodes: any[]
      searchInfo: {
        page: number
        pageCount: number
        facets: string
      }
    }
  }
}

export const usePaginatedProfiles = (options?: { pageSize: number } | null) => {
  const query = useQuery<{
    page?: number
    appliedFacets?: string[]
    queryString?: string
  }>()

  const { search: locationSearch } = useLocation()
  const fetchWithAuth = useFetchWithAuth()

  const toggleFacet = (facet: string, action: 'remove' | 'add') => {
    const valueToRemove = action === 'remove' ? facet : null
    const updatedFacets = flat([[appliedFacets], facet], 2).filter(
      (i) => i !== valueToRemove
    )
    const uniqueFacets = updatedFacets.filter(
      (i, idx) => updatedFacets.indexOf(i) === idx
    )
    query.set({ ...query.search, appliedFacets: uniqueFacets })
  }

  const setQueryString = (queryString: string) =>
    query.set({ ...query.search, queryString, page: 1 })

  const requestedQuery = useDebounce(query.search.queryString ?? '', 450)
  const appliedFacets = query.search.appliedFacets ?? []
  const currentPage = (query.search.page ?? 1) - 1
  const pageSize = options?.pageSize ?? 12

  const { data, isLoading: loading, error } = useReactQuery<
    AdminProfileSearchResponse
  >({
    queryKey: [
      'adminProfileSearch',
      requestedQuery,
      appliedFacets,
      currentPage,
      pageSize,
    ],
    queryFn: async () => {
      // Build query params
      const queryParams = new URLSearchParams()

      if (requestedQuery) queryParams.append('query', requestedQuery)
      if (currentPage !== undefined)
        queryParams.append('page', currentPage.toString())
      if (pageSize !== undefined)
        queryParams.append('limit', pageSize.toString())

      // Add facet filters if present
      if (Array.isArray(appliedFacets) && appliedFacets.length > 0) {
        appliedFacets.forEach((filter) => {
          queryParams.append('facet_filters', filter)
        })
      }

      // Construct the request URL
      const requestUrl = `${API_URL}/admin/profiles/search?${queryParams.toString()}`

      // Fetch profiles from API with filters
      const response = await fetchWithAuth(requestUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      return response as AdminProfileSearchResponse
    },
    staleTime: 60 * 1000, // Data is fresh for 1 minute
  })

  const displayPage = (data?.search?.profiles?.searchInfo?.page ?? 0) + 1
  const pageCount = data?.search?.profiles?.searchInfo?.pageCount ?? 0
  const profiles = data?.search?.profiles?.nodes
  const success = data?.search?.success
  const facets = JSON.parse(data?.search?.profiles?.searchInfo?.facets || '{}')
  const queryString = query.search.queryString

  const handlePageClick = ({ historyPush }: { historyPush?: boolean }) => (
    page: number
  ) => {
    query.set({ ...query.search, page }, historyPush)
  }

  const humanizedAppliedFacets = flat([appliedFacets], 2)
    .map((f) => f.split(':'))
    .map((f) => ({
      id: f.join(':'),
      name: humanize(f[0]),
      value: humanize(f[1]),
    }))

  return {
    appliedFacets,
    currentPage: displayPage,
    pageCount,
    error,
    facets,
    loading,
    profiles,
    queryString,
    setQueryString,
    search: query.search,
    handlePageClick,
    success,
    toggleFacet,
    locationSearch,
    humanizedAppliedFacets,
  }
}
